import * as THREE from 'three';
import { Visualizer } from '../visualizer.js';
import { loadArtworkTexture } from '../artwork-texture.js';
import { safeZoneUniforms } from '../shaders/safeZone.glsl.js';

// Shared-rig contract (see the framing addendum): this piece is lit by the
// image it carries, and its camera relationship is load-bearing geometry.
const RIG = 'forbidden';
export { RIG };

// Orchestrator registry entry (see visualizer-registry.js).
export const PROFILE = {
  id: 'anamorph',
  family: 'object',
  danceability: 0.55,
  energyLevel: 0.55,
  bpmMin: 60,
  bpmMax: 170,
  movement: 0.45,
  tonality: 0.75,
  prefers: { dynamics: [0.25, 1.0] },
  enabled: true
};

// Anamorph: a cloud of fragments scattered through space that, from ONE
// viewpoint, reassembles into a perfect image - Fukuda's and Michael Murphy's
// suspended sculptures. The camera passes through that viewpoint exactly on
// the track's drops, which is the thing no real-time visualiser can do: the
// whole timeline is known in advance, so the resolution is SCHEDULED rather
// than periodic.
//
// CORRECT BY CONSTRUCTION. Scattering fragments and hoping is the trap. The
// construction here is a HOMOTHETY ABOUT THE EYE: a fragment is its image
// cell, translated along the ray from V and scaled by the same factor k.
//
//   fragmentCentre = V + (cellCentre - V) * k
//   fragmentSize   = cellSize * k
//
// A uniform scaling about V leaves every ray through V invariant, so the
// perspective projection from V is IDENTICAL for any k. Not approximately -
// identically, for every cell at once, which is why there is no overlap and
// no gap and nothing to tune.
//
// This is worth stating precisely because the obvious reading of the recipe -
// push along the ray by d0 * k, where d0 is the distance to the image PLANE -
// is subtly wrong. It is right only for the cell on the axis. An off-axis
// cell sits at |cellCentre - V| = d0 / cos(theta), so pushing it to d0 * k
// while scaling it by k makes it subtend d0/cos(theta) / d0 = 1/cos(theta)
// too much angle. At the corners of an 18 x 12 grid theta reaches 27 degrees,
// i.e. fragments 12% oversized, overlapping their neighbours all round the
// edge of the picture. Scaling about V costs exactly the same arithmetic and
// is exact everywhere, so that is what this does.
//
// FRAGMENTS STAY PARALLEL TO THE IMAGE PLANE. "Facing V" is only the same
// thing as "parallel to the image plane" on the axis; a fragment rotated to
// face V is foreshortened relative to its own cell by cos(theta) and the
// homothety's exactness is lost. Parallel is also what the reference actually
// is - Murphy's discs all face the viewer in the same direction, suspended at
// different depths. The deliberate 3-8 degree tilt below is applied on top,
// and is the only thing breaking that alignment.
//
// THE CAMERA IS NEVER TOUCHED. scene-manager applies a Ken Burns drift of
// +-0.22 units to the camera AFTER every update() (see _applyCameraDrift),
// and there is no opt-out. That is fatal here and not by a small margin: an
// eye displaced laterally by 0.22 at d0 = 4.5 sees fragments at k = 0.35 and
// k = 2.8 shift by 0.22/(0.35*4.5) and 0.22/(2.8*4.5) radians respectively -
// a disparity of 7.9 degrees between the nearest and farthest fragments, so
// the picture tears apart exactly when it is supposed to resolve. Rather than
// fight it, the FIELD is placed relative to wherever the camera actually is:
// each frame the group is positioned and oriented so that the camera sits at
// the orbit's current point in field space. The drift then moves camera and
// field together and cancels itself, and nothing depends on scene-manager's
// internal drift clock.

// --- geometry of the construction -------------------------------------------
// V is the field-space origin; the image plane centre M sits at -Z. The orbit
// is a circle of this radius about M passing exactly through V, so the orbit
// amplitude and the eye-to-image distance are the same number.
const ORBIT_RADIUS = 4.5;
const D0 = ORBIT_RADIUS;
// Slight tilt on the orbit plane so the pass through V is not a flat sweep.
const ORBIT_TILT = 0.28;
// How much of the frame height the resolved image fills. Slightly over 1 so
// there is no black margin at the moment it resolves.
const IMAGE_FILL = 1.06;

// Grids per tier. Dropping fragments cannot mean HIDING some of them - that
// would leave holes in the resolved image and fail criterion 1 - so a lower
// tier is a COARSER grid that still tiles the picture completely.
const TIER = {
  0: { cols: 12, rows: 8, edges: false },   // 96
  1: { cols: 16, rows: 10, edges: true },   // 160
  2: { cols: 18, rows: 12, edges: true }    // 216
};
const DEFAULT_TIER = 1;

// Irregular boundaries, so the grid never reads as a chequerboard. Rows get
// jittered heights; within each row the columns get their own jittered widths.
// Boundaries are SHARED between neighbours by construction - each row spans
// the full width and the rows span the full height - so the tiling stays exact
// however hard it is jittered.
const CELL_JITTER = 0.42;

// k ranges, narrow to wide, selected by character.dynamics.
const K_RANGE_CALM = [0.5, 2.0];
const K_RANGE_WILD = [0.35, 2.8];

// The deliberate imperfection. Past ~10 degrees the picture stops resolving.
// 1/cos(8 deg) is 1.0098; a little beyond that closes the worst seam.
const FRAGMENT_OVERSCAN = 1.022;
const TILT_MIN_DEG = 3;
const TILT_MAX_DEG = 8;

// --- motion -----------------------------------------------------------------
const BASE_PERIOD = 50;
const PERIOD_MIN = 25;
const PERIOD_MAX = 90;
// Half-width of the resolution window. At a 50 s period a +-12 degree window
// is 3.3 s, which is the 2-4 s the brief asks for - the two numbers agree.
const ANGLE_MAX = 12 * Math.PI / 180;
const K_WOBBLE = 0.12;
const WOBBLE_ANGLE = 0.20;
const ANTICIPATION_BEATS = 4;
const CONVERGE_PULL = 0.03;
const EDGE_BOOST = 1.20;

// --- motion smoothing (the fix for the reported jerkiness) ---
const CLOCK_SLEW = 0.08;        // max rate warp chasing track time
const CLOCK_SNAP_SEC = 1.5;     // a bigger step is a seek
const AGITATION_TAU = 0.35;     // low-pass on the 100ms-binned band envelope

// --- the show ---
const ROLL_AMP = 0.38;          // rad; the dispersed cloud slowly rolls...
const ROLL_RATE = 0.06;         // ...and de-rotates exactly into the pass
const FLASH_DECAY = 3.2;        // the lock flash, per second
const FLASH_EDGE_GAIN = 1.6;
const FLASH_BODY_GAIN = 0.55;

// --- the beat ---------------------------------------------------------------
// Panels catching the light on the beat. A SUBSET each beat, never all of
// them: every fragment flashing together is a full-frame strobe, and this
// piece already spends most of its time dispersed, so it would be a strobe
// for minutes at a stretch. A fifth of them, re-drawn every beat, reads as
// light moving through the cloud instead.
const FLASH_FRACTION = 0.22;
const FLASH_GAIN = 0.85;
// Squared, so the flash is a sharp onset with a tail rather than a triangle.
const FLASH_DECAY_POW = 2.2;

const ACTIVE_MIN_FRAC = 120 / 216;
const UP = new THREE.Vector3(0, 1, 0);

function clamp01(x) { return Math.max(0, Math.min(1, x)); }
function lerp(a, b, t) { return a + (b - a) * t; }
function urlParam(name) {
  if (typeof location === 'undefined') return null;
  return new URLSearchParams(location.search).get(name);
}
function urlFlag(name) {
  const v = urlParam(name);
  return v !== null && v !== '0' && v !== 'false';
}
// Deterministic per-track RNG, so a track always scatters the same way.
function mulberry(seed) {
  let a = seed >>> 0;
  return function () {
    a = (a + 0x6D2B79F5) >>> 0;
    let t = Math.imul(a ^ (a >>> 15), 1 | a);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

const vertexShader = `
  precision mediump float;

  attribute vec4 aRay;     // xyz unit direction from V, w distance V->cell centre
  attribute vec4 aCell;    // xy cell size, z k, w activation rank
  attribute vec4 aUvRect;  // xy uv origin, zw uv span
  attribute vec4 aTilt;    // fixed 3-8 degree tilt, as a quaternion
  attribute vec4 aJit;     // x wobble rate, y spin phase, z exposure, w drift phase

  uniform float uTime;
  uniform float uAgitation;
  uniform float uConverge;
  uniform float uActiveFrac;
  uniform float uBeat;
  uniform float uBeatSeed;
  uniform float uFlashFrac;

  uniform float uAspect;
  uniform vec2 uSafeCenter;
  uniform vec2 uSafeHalfSize;
  uniform float uSafeFeather;

  varying vec2 vUv;
  varying vec2 vLocal;
  varying float vExposure;
  varying float vDepth;
  varying float vSafe;
  varying float vK;
  varying float vFlash;

  vec3 qrot(vec4 q, vec3 v) {
    return v + 2.0 * cross(q.xyz, cross(q.xyz, v) + q.w * v);
  }

  // Own copy of the shared chunk: that one bakes a 0.32 floor, and at 0.32 a
  // black hole opens in the middle of the picture at the exact moment it
  // resolves - the resolved image is centred and far larger than the DOM
  // cover, so the safe rectangle lands in the middle of the subject rather
  // than on a background.
  float safeZoneAttenuation(vec4 clipPos) {
    vec2 ndc = clipPos.xy / max(clipPos.w, 0.0001);
    vec2 s = vec2(ndc.x * uAspect, ndc.y);
    vec2 q = abs(s - uSafeCenter) - uSafeHalfSize;
    float d = length(max(q, 0.0)) + min(max(q.x, q.y), 0.0);
    return mix(0.45, 1.0, smoothstep(0.0, uSafeFeather, d));
  }

  void main() {
    // THE HOMOTHETY. Position and size share one factor, so the projection
    // from V does not depend on it at all - which is what lets k drift with
    // the music without the resolved image ever moving.
    //
    // The drift along the ray and the wobble are both multiplied by
    // uAgitation, which the host drives to zero as the pass approaches: the
    // agitation therefore cancels EXACTLY at the resolution rather than
    // merely becoming small.
    float k = aCell.z
      * (1.0 + uAgitation * ${K_WOBBLE.toFixed(3)} * sin(uTime * aJit.x + aJit.w))
      * uConverge;

    // Activation rank, not a change of instance count: the geometry is built
    // once and a dormant fragment is simply scaled to nothing.
    // 'active' is a reserved word in GLSL.
    float alive = step(aCell.w, uActiveFrac);

    vec3 centre = aRay.xyz * (aRay.w * k);
    vec2 size = aCell.xy * (k * alive);

    // In-plane wobble. An oscillation rather than an accumulating spin,
    // because a spin that has to vanish at the resolution has to be able to
    // RETURN to its rest orientation, and an accumulating one cannot.
    float ang = uAgitation * ${WOBBLE_ANGLE.toFixed(3)} * sin(uTime * aJit.x * 0.7 + aJit.y);
    float ca = cos(ang), sa = sin(ang);
    vec2 q2 = position.xy * size;
    vec3 local = vec3(q2.x * ca - q2.y * sa, q2.x * sa + q2.y * ca, 0.0);

    vec3 pos = centre + qrot(aTilt, local);
    vec4 mv = modelViewMatrix * vec4(pos, 1.0);
    gl_Position = projectionMatrix * mv;

    // Which panels catch the light this beat. aCell.w is already a uniform
    // random per fragment, and uBeatSeed changes once per beat, so one hash
    // re-draws the subset every beat with no state anywhere.
    //
    // Both terms are kept small on purpose: mediump is real fp16 on the
    // television, where fract() of a large argument has no fractional part
    // left. 37.0 keeps this under 38 and still leaves ~5 bits, which is
    // ample for a threshold test.
    float h = fract(aCell.w * 37.0 + uBeatSeed);
    vFlash = step(h, uFlashFrac) * uBeat;

    vUv = aUvRect.xy + uv * aUvRect.zw;
    vLocal = uv;
    vExposure = aJit.z;
    vDepth = -mv.z;
    vK = aCell.z;
    vSafe = safeZoneAttenuation(gl_Position);
  }
`;

function buildFragmentShader(edges) {
  return `
  precision mediump float;

  uniform sampler2D uTarget;
  uniform vec3 uEdgeColor;
  uniform vec3 uFogColor;
  uniform float uEdgeGain;
  uniform float uCoherence;
  uniform float uKDebug;
  uniform float uFlash;
  uniform vec2 uKRange;
  uniform float uAtmoNear;
  uniform float uAtmoScale;

  varying vec2 vUv;
  varying vec2 vLocal;
  varying float vExposure;
  varying float vDepth;
  varying float vSafe;
  varying float vK;
  varying float vFlash;

  void main() {
    if (uKDebug > 0.5) {
      float g = (vK - uKRange.x) / max(uKRange.y - uKRange.x, 0.001);
      gl_FragColor = vec4(vec3(clamp(g, 0.0, 1.0)) * vSafe, 1.0);
      return;
    }

    // The fragment carries its own piece of the picture even when scattered.
    // That matters more than it sounds: this visualiser spends most of its
    // time NOT resolved, so the dispersed state has to be worth looking at,
    // and shards that already carry the cover's colours are what make it so.
    vec3 col = texture2D(uTarget, vUv).rgb * vExposure;
    // The lock flash: the whole image lifts for a beat as it snaps in.
    col *= 1.0 + uFlash * ${FLASH_BODY_GAIN.toFixed(2)};
    // The panel itself lifts, tinted toward the cover's own hue rather than
    // white, so the flash belongs to the picture instead of washing it out.
    col += (uEdgeColor * 0.55 + vec3(0.30)) * (vFlash * ${FLASH_GAIN.toFixed(2)});
${edges ? `
    // Edges light up as the resolution approaches. The distance to the
    // nearest border is in the quad's own uv, so the liseré is a constant
    // fraction of each fragment rather than a constant number of pixels -
    // which keeps a near fragment and a far one looking like the same object.
    vec2 d = min(vLocal, 1.0 - vLocal);
    float edge = 1.0 - smoothstep(0.0, 0.055, min(d.x, d.y));
    // The border takes the flash harder than the face - that is what makes a
    // flat panel read as catching a light rather than simply getting brighter.
    col += uEdgeColor * edge * (uEdgeGain + vFlash * 1.6);
` : ``}
    // Aerial perspective: distant fragments lose contrast toward the fog
    // rather than simply dimming, so depth reads even where the cloud is thin.
    float atmo = exp(-max(vDepth - uAtmoNear, 0.0) * uAtmoScale);
    col = mix(uFogColor, col, clamp(atmo, 0.0, 1.0));

    gl_FragColor = vec4(col * vSafe, 1.0);
  }
`;
}

const bgVertexShader = `
  precision mediump float;
  varying vec2 vUv;
  void main() {
    vUv = uv;
    gl_Position = vec4(position.xy * 2.0, 0.0, 1.0);
  }
`;

const bgFragmentShader = `
  precision mediump float;
  uniform vec3 uTint;
  uniform float uAspect;
  varying vec2 vUv;
  void main() {
    vec2 p = (vUv - 0.5) * vec2(uAspect, 1.0);
    float g = 1.0 - clamp(length(p) * 1.25, 0.0, 1.0);
    vec3 col = uTint * g * g * 0.055;
    // Wrapped to a 64px tile: mediump is real fp16 on the television, where
    // fract() of a raw gl_FragCoord product has no fractional part left.
    vec2 fcw = mod(floor(gl_FragCoord.xy), 64.0);
    float dith = (fract(dot(fcw, vec2(0.7548776662, 0.5698402909))) - 0.5) / 255.0;
    gl_FragColor = vec4(max(col + dith, vec3(0.0)), 1.0);
  }
`;

export class AnamorphVisualizer extends Visualizer {
  constructor(options) {
    super();
    const tier = options && options.qualityTier != null ? options.qualityTier : DEFAULT_TIER;
    this._tier = TIER[tier] ? tier : DEFAULT_TIER;
    this._lock = urlFlag('anamorphlock');
    this._kDebug = urlFlag('anamorphk');
    this._info = urlFlag('anamorphinfo');
  }

  init(scene, camera) {
    this._scene = scene;
    this._camera = camera;
    const layout = TIER[this._tier];
    this._cols = layout.cols;
    this._rows = layout.rows;
    const count = this._cols * this._rows;
    this._count = count;

    // Field-space landmarks. V is the origin by construction.
    this._V = new THREE.Vector3(0, 0, 0);
    this._M = new THREE.Vector3(0, 0, -D0);

    // The image is sized to fill the frame height from V, which is what makes
    // the resolved picture much larger than the DOM cover.
    const fov = (camera && camera.fov ? camera.fov : 68) * Math.PI / 180;
    this._imageHeight = 2 * D0 * Math.tan(fov / 2) * IMAGE_FILL;
    this._imageWidth = this._imageHeight; // square until a target says otherwise

    this._group = new THREE.Group();
    this._group.matrixAutoUpdate = false;
    scene.add(this._group);

    this._geometry = new THREE.InstancedBufferGeometry();
    const quad = new THREE.PlaneGeometry(1, 1);
    this._geometry.index = quad.index;
    this._geometry.attributes.position = quad.attributes.position;
    this._geometry.attributes.uv = quad.attributes.uv;
    quad.dispose();

    this._aRay = new Float32Array(count * 4);
    this._aCell = new Float32Array(count * 4);
    this._aUvRect = new Float32Array(count * 4);
    this._aTilt = new Float32Array(count * 4);
    this._aJit = new Float32Array(count * 4);

    this._kRange = K_RANGE_CALM.slice();
    this._buildField(0);

    this._geometry.setAttribute('aRay', new THREE.InstancedBufferAttribute(this._aRay, 4));
    this._geometry.setAttribute('aCell', new THREE.InstancedBufferAttribute(this._aCell, 4));
    this._geometry.setAttribute('aUvRect', new THREE.InstancedBufferAttribute(this._aUvRect, 4));
    this._geometry.setAttribute('aTilt', new THREE.InstancedBufferAttribute(this._aTilt, 4));
    this._geometry.setAttribute('aJit', new THREE.InstancedBufferAttribute(this._aJit, 4));
    this._geometry.instanceCount = count;

    this._target = this._makeFallbackTarget();

    this._uniforms = Object.assign({}, safeZoneUniforms, {
      uTime: { value: 0 },
      uAgitation: { value: 1 },
      uConverge: { value: 1 },
      uActiveFrac: { value: 1 },
      uBeat: { value: 0 },
      uBeatSeed: { value: 0 },
      uFlashFrac: { value: FLASH_FRACTION },
      uCoherence: { value: 0 },
      uTarget: { value: this._target },
      uEdgeColor: { value: new THREE.Color(0xfff0d0) },
      uFogColor: { value: new THREE.Color(0x0a0c14) },
      uEdgeGain: { value: 0 },
      uKDebug: { value: this._kDebug ? 1 : 0 },
      uFlash: { value: 0 },
      uKRange: { value: new THREE.Vector2(this._kRange[0], this._kRange[1]) },
      uAtmoNear: { value: D0 * 0.6 },
      uAtmoScale: { value: 0.055 }
    });

    this._material = new THREE.ShaderMaterial({
      uniforms: this._uniforms,
      vertexShader: vertexShader,
      fragmentShader: buildFragmentShader(layout.edges),
      side: THREE.DoubleSide,
      transparent: false,
      depthTest: true,
      depthWrite: true
    });
    this._mesh = new THREE.Mesh(this._geometry, this._material);
    this._mesh.frustumCulled = false;
    this._group.add(this._mesh);

    // Background: its own trivial pass, drawn first.
    this._bgUniforms = {
      uTint: { value: new THREE.Color(0x2a3550) },
      uAspect: safeZoneUniforms.uAspect
    };
    this._bgGeometry = new THREE.PlaneGeometry(1, 1);
    this._bgMaterial = new THREE.ShaderMaterial({
      uniforms: this._bgUniforms,
      vertexShader: bgVertexShader,
      fragmentShader: bgFragmentShader,
      depthTest: false,
      depthWrite: false
    });
    this._bgMesh = new THREE.Mesh(this._bgGeometry, this._bgMaterial);
    this._bgMesh.frustumCulled = false;
    this._bgMesh.renderOrder = -1000;
    scene.add(this._bgMesh);

    // Orbit schedule.
    this._period = BASE_PERIOD;
    this._phase = 0;            // track-time of a pass through V
    this._targetDropSec = null;
    this._lastTrackId = undefined;
    this._lastPaletteRef = undefined;
    this._lastArtworkUrl = undefined;
    this._artworkTexture = null;
    this._useTitle = false;
    this._coherence = 0;
    this._agitation = 1;
    this._trackTime = 0;
    this._clock = 0;
    this._roll = 0;
    this._flash = 0;
    this._prevCoherence = 0;

    // Scratch, so update() allocates nothing.
    this._sP = new THREE.Vector3();
    this._sAxis = new THREE.Vector3();
    this._sTmp = new THREE.Vector3();
    this._sQuat = new THREE.Quaternion();
    this._sQuat2 = new THREE.Quaternion();
    this._sMat = new THREE.Matrix4();
    this._sQuatField = new THREE.Quaternion();
    this._sRollQ = new THREE.Quaternion();
    this._sUp = new THREE.Vector3();

    if (this._info) this._makeInfoOverlay();
    this._placeGroup(0);
  }

  // --- the construction -----------------------------------------------------
  // Everything positional is computed HERE, once. update() writes uniforms and
  // one group matrix and nothing else.
  _buildField(seed) {
    const rng = mulberry(0x9E3779B9 ^ (seed >>> 0));
    const cols = this._cols, rows = this._rows;
    const W = this._imageWidth, H = this._imageHeight;

    // Row heights, jittered but summing to exactly H.
    const rowH = new Float32Array(rows);
    let sum = 0;
    for (let j = 0; j < rows; j++) {
      rowH[j] = 1 + (rng() * 2 - 1) * CELL_JITTER;
      sum += rowH[j];
    }
    const tilt = new THREE.Quaternion();
    const axis = new THREE.Vector3();
    let n = 0;
    let y = H / 2;
    for (let j = 0; j < rows; j++) {
      const h = (rowH[j] / sum) * H;
      const cy = y - h / 2;
      // v of the cell's BOTTOM edge, in 0..1 up the image.
      const v0 = (y - h + H / 2) / H;
      const vSpan = h / H;
      // Column widths for THIS row - independent per row, which is what stops
      // the grid reading as a lattice, and still tiles the row exactly.
      const colW = new Float32Array(cols);
      let csum = 0;
      for (let i = 0; i < cols; i++) {
        colW[i] = 1 + (rng() * 2 - 1) * CELL_JITTER;
        csum += colW[i];
      }
      let x = -W / 2;
      for (let i = 0; i < cols; i++) {
        const w = (colW[i] / csum) * W;
        const cx = x + w / 2;
        const o = n * 4;

        // Ray from V through the cell centre. Distance is the TRUE distance to
        // the cell centre, not to the plane - see the header.
        const dx = cx, dy = cy, dz = -D0;
        const dist = Math.sqrt(dx * dx + dy * dy + dz * dz);
        this._aRay[o] = dx / dist;
        this._aRay[o + 1] = dy / dist;
        this._aRay[o + 2] = dz / dist;
        this._aRay[o + 3] = dist;

        const k = lerp(this._kRange[0], this._kRange[1], rng());
        // A hair oversized. Tilting a quad about its own centre shrinks its
        // projected footprint by cos(tilt), which at 3-8 degrees opens a
        // hairline of background between every pair of neighbours - measured
        // at 1.6% of the picture. The seams are WANTED (they are the
        // deliberate imperfection) but they should read as joins in a
        // sculpture, not as gaps, so the fragments overlap by slightly more
        // than the worst-case shrink and the seams become offsets rather
        // than holes.
        this._aCell[o] = w * FRAGMENT_OVERSCAN;
        this._aCell[o + 1] = h * FRAGMENT_OVERSCAN;
        this._aCell[o + 2] = k;
        this._aCell[o + 3] = rng();          // activation rank

        this._aUvRect[o] = (cx - w / 2 + W / 2) / W;
        this._aUvRect[o + 1] = v0;
        this._aUvRect[o + 2] = w / W;
        this._aUvRect[o + 3] = vSpan;

        // The deliberate imperfection: 3-8 degrees about a random axis, so the
        // resolved picture keeps slight offsets at the joins the way a real
        // sculpture does.
        axis.set(rng() * 2 - 1, rng() * 2 - 1, rng() * 2 - 1);
        if (axis.lengthSq() < 1e-6) axis.set(0, 0, 1);
        axis.normalize();
        const deg = lerp(TILT_MIN_DEG, TILT_MAX_DEG, rng());
        tilt.setFromAxisAngle(axis, deg * Math.PI / 180);
        this._aTilt[o] = tilt.x;
        this._aTilt[o + 1] = tilt.y;
        this._aTilt[o + 2] = tilt.z;
        this._aTilt[o + 3] = tilt.w;

        this._aJit[o] = 0.18 + rng() * 0.35;          // wobble rate
        this._aJit[o + 1] = rng() * Math.PI * 2;      // spin phase
        this._aJit[o + 2] = 0.82 + rng() * 0.36;      // exposure
        this._aJit[o + 3] = rng() * Math.PI * 2;      // ray-drift phase

        x += w;
        n++;
      }
      y -= h;
    }
    this._uvNeedsRebuild = false;
  }

  _markAttributesDirty() {
    const g = this._geometry;
    if (!g || !g.attributes.aRay) return;
    g.attributes.aRay.needsUpdate = true;
    g.attributes.aCell.needsUpdate = true;
    g.attributes.aUvRect.needsUpdate = true;
    g.attributes.aTilt.needsUpdate = true;
    g.attributes.aJit.needsUpdate = true;
  }

  // --- the target image -----------------------------------------------------
  _makeFallbackTarget() {
    const cv = document.createElement('canvas');
    cv.width = 512; cv.height = 512;
    const x = cv.getContext('2d');
    x.fillStyle = '#0a0a12'; x.fillRect(0, 0, 512, 512);
    for (let i = 0; i < 7; i++) {
      x.strokeStyle = `hsl(${(i * 37) % 360} 80% ${45 + i * 4}%)`;
      x.lineWidth = 14;
      x.beginPath(); x.arc(256, 256, 40 + i * 30, 0, Math.PI * 2); x.stroke();
    }
    const t = new THREE.CanvasTexture(cv);
    t.colorSpace = THREE.SRGBColorSpace;
    t.needsUpdate = true;
    return t;
  }

  // The title, very large, one or two lines. Empty title falls back to artist,
  // then album, then the geometric pattern.
  _makeTitleTarget(profile) {
    const text = (profile.trackTitle || profile.artist || profile.album || '').trim();
    if (!text) return null;
    const S = 1024;
    const cv = document.createElement('canvas');
    cv.width = S; cv.height = S;
    const x = cv.getContext('2d');
    x.fillStyle = '#07070c'; x.fillRect(0, 0, S, S);
    x.textAlign = 'center';
    x.textBaseline = 'middle';
    // One line if it fits at a large size, otherwise split on the nearest
    // space to the middle.
    let lines = [text];
    let size = 300;
    x.font = `800 ${size}px Inter, Helvetica, Arial, sans-serif`;
    if (x.measureText(text).width > S * 0.88) {
      const mid = text.length / 2;
      let best = -1, bestD = 1e9;
      for (let i = 0; i < text.length; i++) {
        if (text[i] === ' ') {
          const d = Math.abs(i - mid);
          if (d < bestD) { bestD = d; best = i; }
        }
      }
      lines = best > 0 ? [text.slice(0, best), text.slice(best + 1)] : [text];
    }
    size = 300;
    for (;;) {
      x.font = `800 ${size}px Inter, Helvetica, Arial, sans-serif`;
      let wide = 0;
      for (let i = 0; i < lines.length; i++) wide = Math.max(wide, x.measureText(lines[i]).width);
      if (wide <= S * 0.88 || size <= 40) break;
      size -= 8;
    }
    x.fillStyle = '#fdfdff';
    const lh = size * 1.02;
    const y0 = S / 2 - (lines.length - 1) * lh / 2;
    for (let i = 0; i < lines.length; i++) x.fillText(lines[i], S / 2, y0 + i * lh);
    const t = new THREE.CanvasTexture(cv);
    t.colorSpace = THREE.SRGBColorSpace;
    t.needsUpdate = true;
    return t;
  }

  _setTarget(tex) {
    if (!tex || tex === this._target) return;
    const old = this._target;
    this._target = tex;
    this._uniforms.uTarget.value = tex;
    if (old) old.dispose();
  }

  // --- scheduling the passes onto the drops ---------------------------------
  // Solve for the period and phase that put a pass through V as close as
  // possible to each of the strongest drops, subject to the spacing bounds.
  // Passes are at phase + n*period, so "spacing" IS the period.
  _scheduleFromMarks(timeline) {
    this._targetDropSec = null;
    this._drops = null;
    const marks = timeline && timeline.marks;
    if (!marks || !marks.length) {
      this._period = BASE_PERIOD;
      this._phase = 0;
      return;
    }
    // The 3-5 highest scoring, preferring real drops over rises and falls.
    const sorted = marks.slice().sort(function (a, b) {
      const ta = a.type === 'drop' ? 1 : 0, tb = b.type === 'drop' ? 1 : 0;
      if (ta !== tb) return tb - ta;
      return b.score - a.score;
    }).slice(0, 5);
    if (!sorted.length) {
      this._period = BASE_PERIOD;
      this._phase = 0;
      return;
    }
    const t = [];
    for (let i = 0; i < sorted.length; i++) t.push(sorted[i].atMs / 1000);

    // Brute force over the admissible periods. The cost is the sum of squared
    // circular distances from each drop to the nearest pass; for a given
    // period the best phase is the circular MEAN of the drops' positions
    // within it, which is exact rather than searched.
    let bestCost = Infinity, bestT = BASE_PERIOD, bestPhase = 0;
    for (let P = PERIOD_MIN; P <= PERIOD_MAX + 1e-6; P += 0.25) {
      let sx = 0, sy = 0;
      for (let i = 0; i < t.length; i++) {
        const a = 2 * Math.PI * ((t[i] % P) / P);
        sx += Math.cos(a); sy += Math.sin(a);
      }
      if (sx === 0 && sy === 0) continue;
      let mean = Math.atan2(sy, sx);
      if (mean < 0) mean += 2 * Math.PI;
      const phase = P * mean / (2 * Math.PI);
      let cost = 0;
      for (let i = 0; i < t.length; i++) {
        let d = (t[i] - phase) % P;
        if (d < 0) d += P;
        if (d > P / 2) d = P - d;
        cost += d * d;
      }
      if (cost < bestCost) { bestCost = cost; bestT = P; bestPhase = phase; }
    }
    this._period = bestT;
    this._phase = bestPhase;
    this._drops = t;
  }

  _nextPassSec(now) {
    const P = this._period;
    let n = Math.ceil((now - this._phase) / P);
    if (!isFinite(n)) return now;
    return this._phase + n * P;
  }

  // --- placing the field ----------------------------------------------------
  // The camera is authoritative; the field moves so that the camera occupies
  // the orbit's current point IN FIELD SPACE. See the header for why.
  _placeGroup(theta) {
    const cam = this._camera;
    if (!cam) return;
    // Orbit point in field space: a circle about M of radius ORBIT_RADIUS in
    // the plane spanned by (M -> V) and a tilted perpendicular axis, so
    // theta = 0 lands exactly on V.
    const ax = Math.cos(theta), sx = Math.sin(theta);
    const a = this._sAxis.set(1, ORBIT_TILT, 0).normalize();
    this._sP.set(
      this._M.x + ORBIT_RADIUS * (a.x * sx),
      this._M.y + ORBIT_RADIUS * (a.y * sx),
      this._M.z + ORBIT_RADIUS * (ax + a.z * sx)
    );

    // Orientation the camera would have in field space, looking from P at M.
    //
    // Matrix4.lookAt directly, NOT Object3D.lookAt: three.js swaps that
    // method's arguments for anything that is not a camera or a light, so a
    // plain Object3D ends up pointing its +Z at the target while a camera
    // points its -Z. Using an Object3D here silently built a frame rotated
    // 180 degrees, which put the whole image behind the camera - measured,
    // the reference plane landed at z = +10.5 with the camera at z = +6.
    // The swirl: UP rotated about the sight axis by the current roll. Zero
    // at resolution by construction (see update), so the reference framing is
    // exactly the brief's when it matters.
    this._sAxis.copy(this._M).sub(this._sP).normalize();
    this._sRollQ.setFromAxisAngle(this._sAxis, this._roll);
    this._sUp.set(0, 1, 0).applyQuaternion(this._sRollQ);
    this._sMat.lookAt(this._sP, this._M, this._sUp);
    this._sQuatField.setFromRotationMatrix(this._sMat);

    // group = camWorld * fieldCam^-1, so that field-space camera -> real camera.
    cam.updateMatrixWorld();
    this._sQuat.copy(this._sQuatField).invert();
    this._sQuat2.copy(cam.quaternion).multiply(this._sQuat);
    this._sTmp.copy(this._sP).applyQuaternion(this._sQuat2);

    this._group.quaternion.copy(this._sQuat2);
    this._group.position.copy(cam.position).sub(this._sTmp);
    this._group.updateMatrix();
    this._group.updateMatrixWorld(true);
  }

  _makeInfoOverlay() {
    const el = document.createElement('div');
    el.style.cssText = 'position:fixed;left:10px;top:10px;z-index:99999;'
      + 'font:12px/1.5 ui-monospace,Menlo,monospace;color:#9fe;'
      + 'background:rgba(0,0,0,.65);padding:8px 10px;border-radius:6px;white-space:pre';
    document.body.appendChild(el);
    this._infoEl = el;
    this._infoAccum = 0;
  }

  onTrackChange(profile) {
    this._scheduleFromMarks(profile && profile.analysisTimeline);
  }

  update(deltaTime, profile) {
    // Pause is a complete freeze, orbit included.
    if (profile.playbackState !== 'PLAYING') return;

    const director = profile.director;
    const bands = director && director.bands;
    const character = director && director.character;

    // --- track identity: rebuild the scatter and re-solve the schedule ---
    if (profile.trackId !== this._lastTrackId) {
      this._lastTrackId = profile.trackId;
      let seed = 0;
      const id = String(profile.trackId || '');
      for (let i = 0; i < id.length; i++) seed = (seed * 31 + id.charCodeAt(i)) | 0;
      this._seed = seed;
      // Alternate between the cover and the title from track to track; with no
      // cover the title is the only option.
      this._useTitle = !profile.artworkUrl || ((seed >>> 3) & 1) === 1;
      this._scheduleFromMarks(profile.analysisTimeline);
      const dyn = character && character.dynamics != null ? character.dynamics : 0.5;
      this._kRange[0] = lerp(K_RANGE_CALM[0], K_RANGE_WILD[0], clamp01(dyn));
      this._kRange[1] = lerp(K_RANGE_CALM[1], K_RANGE_WILD[1], clamp01(dyn));
      this._uniforms.uKRange.value.set(this._kRange[0], this._kRange[1]);
      this._buildField(seed);
      this._markAttributesDirty();
      if (this._useTitle) {
        const t = this._makeTitleTarget(profile);
        if (t) this._setTarget(t);
      }
    }

    if (profile.artworkUrl !== this._lastArtworkUrl) {
      this._lastArtworkUrl = profile.artworkUrl;
      if (profile.artworkUrl && !this._useTitle) {
        const self = this;
        loadArtworkTexture(profile.artworkUrl, { wrap: THREE.ClampToEdgeWrapping, mipmaps: true },
          function (tex) { self._setTarget(tex); },
          function () { /* keep whatever is on screen */ });
      }
    }

    if (profile.artworkPalette !== this._lastPaletteRef) {
      this._lastPaletteRef = profile.artworkPalette;
      const vivid = profile.artworkPalette && profile.artworkPalette.vividColors;
      if (vivid && vivid.length) {
        this._uniforms.uEdgeColor.value.set(vivid[0]);
        this._bgUniforms.uTint.value.set(vivid[vivid.length - 1]);
        this._uniforms.uFogColor.value.set(vivid[vivid.length - 1]).multiplyScalar(0.06);
      }
    }

    // --- the clock. Track time, because the drops are at track times - but
    // SLEWED, never raw. estimatedPosition re-anchors on every Sonos poll,
    // and each correction stepped the orbit angle and the wobble time in one
    // frame: the whole cloud visibly hopped every few seconds - the reported
    // jerkiness. The render clock advances by deltaTime (perfectly smooth)
    // and converges on track time at up to 8% rate warp, which corrects a
    // 300ms poll step in ~4s - long before a +-12 degree resolution window
    // could notice. A step over 1.5s is a seek: snap.
    const target = profile.estimatedPosition != null
      ? profile.estimatedPosition
      : (this._clock + deltaTime);
    let now;
    if (Math.abs(target - this._clock) > CLOCK_SNAP_SEC || this._clock === 0) {
      now = target;
    } else {
      const err = target - (this._clock + deltaTime);
      now = this._clock + deltaTime
        + Math.max(-CLOCK_SLEW * deltaTime, Math.min(CLOCK_SLEW * deltaTime, err));
    }
    this._clock = now;
    this._trackTime = now;

    // --- orbit angle and coherence ---
    let theta;
    if (this._lock) {
      theta = 0;
    } else {
      theta = 2 * Math.PI * ((now - this._phase) / this._period);
    }
    // Signed angular distance to the nearest pass, which IS the angle between
    // (camera - M) and (V - M) since both are radii of the same circle.
    let off = theta % (2 * Math.PI);
    if (off > Math.PI) off -= 2 * Math.PI;
    if (off < -Math.PI) off += 2 * Math.PI;
    const coherence = 1 - clamp01(Math.abs(off) / ANGLE_MAX);
    this._coherence = coherence;

    // --- anticipation: the last 4 beats before the pass ---
    const bpm = (profile.trackFeatures && profile.trackFeatures.bpm) || 120;
    const window = ANTICIPATION_BEATS * 60 / bpm;
    const tNext = this._nextPassSec(now);
    const untilPass = tNext - now;
    // Which drop that pass is aimed at - the nearest one to it.
    if (this._drops) {
      let best = null, bestD = Infinity;
      for (let i = 0; i < this._drops.length; i++) {
        const d = Math.abs(this._drops[i] - tNext);
        if (d < bestD) { bestD = d; best = this._drops[i]; }
      }
      this._targetDropSec = best;
    }
    const antic = clamp01(1 - untilPass / Math.max(window, 0.001));

    // Agitation dies before the resolution, not merely at it, and comes back
    // after: whichever of the two is stronger wins.
    //
    // LOW-PASSED. bands.low is 100ms-binned upstream, so feeding it straight
    // into the wobble AMPLITUDE stepped every fragment's motion ten times a
    // second - the other half of the reported jerkiness. The settle term
    // stays outside the filter: the freeze at resolution must be exact, not
    // smoothed into lateness.
    const low = bands && bands.low != null ? clamp01(bands.low) : 0.5;
    const settle = Math.max(coherence, antic);
    const kAgit = 1 - Math.exp(-deltaTime / AGITATION_TAU);
    this._agitationBase = (this._agitationBase == null ? (0.55 + 0.9 * low)
      : this._agitationBase + ((0.55 + 0.9 * low) - this._agitationBase) * kAgit);
    this._agitation = this._agitationBase * (1 - settle);

    // --- the lock flash: the instant the picture snaps into place ---
    if (coherence > 0.9 && this._prevCoherence <= 0.9) this._flash = 1;
    this._prevCoherence = coherence;
    this._flash *= Math.exp(-deltaTime * FLASH_DECAY);

    // --- the swirl: the whole constellation slowly rolls about the view
    // axis while dispersed, and de-rotates into place as the pass approaches.
    // Scaled by (1 - settle), so at the resolution it is EXACTLY zero and the
    // anamorphic alignment is untouched.
    this._roll = ROLL_AMP * Math.sin(now * ROLL_RATE) * (1 - settle);

    this._uniforms.uTime.value = now;
    this._uniforms.uAgitation.value = this._agitation;
    this._uniforms.uCoherence.value = coherence;
    this._uniforms.uFlash.value = this._flash;
    // The extra 3% convergence in the run-up: k is pulled toward 1, i.e.
    // every fragment eases a little way back toward its own cell.
    this._uniforms.uConverge.value = 1 - CONVERGE_PULL * settle;

    // Edges: bands.high sets the base, coherence lights them up. The bloom
    // pass itself is applied per-SWAP by scene-manager (_applyPostProfile) and
    // cannot be animated per frame, so the brief's 20% bloom rise in the
    // run-up is bought here instead - brighter edges above the 0.78 bloom
    // threshold is what actually feeds that pass.
    const high = bands && bands.high != null ? clamp01(bands.high) : 0.4;
    this._uniforms.uEdgeGain.value = (0.10 + 0.55 * high) * (0.25 + 0.75 * settle) * EDGE_BOOST
      + this._flash * FLASH_EDGE_GAIN;

    // beat -> a fifth of the panels catch the light, a different fifth each
    // beat. Stateless: beatPhase gives the decay within the beat and
    // beatIndex re-seeds the subset, so there is no onset to detect and
    // nothing to get out of step after a seek.
    //
    // Damped by the same (1 - settle) as the agitation, so the flashing stops
    // before the picture resolves. A subset of panels flashing across a
    // resolved image would break exactly the flatness the whole construction
    // exists to produce.
    const bp = profile.beatPhase;
    if (bp != null) {
      this._uniforms.uBeat.value = Math.pow(1 - clamp01(bp), FLASH_DECAY_POW) * (1 - settle);
      const bi = profile.beatIndex != null ? profile.beatIndex : 0;
      // Re-seeded per beat; kept in [0,1) here rather than in the shader,
      // where a large beat index would lose its fractional part in fp16.
      this._uniforms.uBeatSeed.value = (bi * 0.7548776662) % 1;
    } else {
      // No beat grid, no beat effect - a free-running blink against an
      // unknown tempo is worse than none.
      this._uniforms.uBeat.value = 0;
    }
    this._uniforms.uFlashFrac.value = FLASH_FRACTION * (0.65 + 0.7 * high);

    // progress -> how much of the cloud is present, 120 at the edges to the
    // full set by the middle. Forced to full as the picture resolves: a
    // dormant fragment is a HOLE in the resolved image, which criterion 1
    // forbids, so the missing ones arrive during the run-up.
    const progress = profile.duration > 0 ? clamp01(profile.progress) : 0.5;
    const byProgress = lerp(ACTIVE_MIN_FRAC, 1, Math.min(1, Math.sin(Math.PI * progress) * 1.6));
    this._uniforms.uActiveFrac.value = Math.max(byProgress, settle);

    this._placeGroup(theta);

    if (this._infoEl) {
      this._infoAccum += deltaTime;
      if (this._infoAccum > 0.1) {
        this._infoAccum = 0;
        this._infoEl.textContent =
          'coherence  ' + coherence.toFixed(3) + '\n' +
          'next pass  ' + untilPass.toFixed(2) + ' s\n' +
          'target     ' + (this._targetDropSec != null ? this._targetDropSec.toFixed(2) + ' s' : '(free run)') + '\n' +
          'period     ' + this._period.toFixed(2) + ' s\n' +
          'phase      ' + this._phase.toFixed(2) + ' s\n' +
          'now        ' + now.toFixed(2) + ' s\n' +
          'agitation  ' + this._agitation.toFixed(3) + '\n' +
          'active     ' + this._uniforms.uActiveFrac.value.toFixed(3);
      }
    }
  }

  dispose() {
    this._scene.remove(this._group);
    this._scene.remove(this._bgMesh);
    this._geometry.dispose();
    this._material.dispose();
    this._bgGeometry.dispose();
    this._bgMaterial.dispose();
    if (this._target) this._target.dispose();
    if (this._infoEl && this._infoEl.parentNode) this._infoEl.parentNode.removeChild(this._infoEl);
  }
}
